/** @format */

import axios, { AxiosRequestConfig } from "axios";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";
import {
    ISegmentCounts,
    IBehaviorListResponse,
    ICustomerBehavior,
    IRecommendationListResponse,
    IRecommendation,
    IRecomputeIntelligenceResult,
    CustomerSegment,
    RecommendationStatus,
    RecommendationType,
} from "../../utility/types/collectTypes";

/**
 * Stage 2I — customer behaviour segmentation + recommendations service layer.
 *
 * Self-contained API client (same convention as `collectTaskService.ts`).
 * Auth bearer is injected by the global axios interceptor; the tenant header
 * (`x-business-id`) is passed explicitly here.
 */

function withBusiness(
    businessId: string,
    extra: AxiosRequestConfig = {},
): AxiosRequestConfig {
    return {
        ...extra,
        headers: { ...(extra.headers || {}), "x-business-id": businessId },
    };
}

function pickError(error: any, fallback: string): string {
    return error?.response?.data?.message || error?.message || fallback;
}

// ── Behaviour / segments ────────────────────────────────────────────────

export const fetchSegmentCounts = async (
    businessId: string,
): Promise<ISegmentCounts> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/intelligence/segments`,
            withBusiness(businessId),
        );
        return res.data?.data as ISegmentCounts;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load segments."));
    }
};

export interface IListBehaviorQuery {
    segment?: CustomerSegment;
    page?: number;
    limit?: number;
}

export const fetchBehaviorProfiles = async (
    businessId: string,
    query: IListBehaviorQuery = {},
): Promise<IBehaviorListResponse> => {
    try {
        const params: Record<string, any> = {};
        if (query.segment) params.segment = query.segment;
        if (query.page) params.page = query.page;
        if (query.limit) params.limit = query.limit;
        const res = await axios.get(
            `${API_BASE_URL}/collect/intelligence/behavior`,
            withBusiness(businessId, { params }),
        );
        return res.data?.data as IBehaviorListResponse;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load behaviour profiles."));
    }
};

export const fetchCustomerBehavior = async (
    businessId: string,
    customerId: string,
): Promise<ICustomerBehavior> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/intelligence/behavior/${customerId}`,
            withBusiness(businessId),
        );
        return res.data?.data as ICustomerBehavior;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load customer behaviour."));
    }
};

// ── Recommendations ─────────────────────────────────────────────────────

export interface IListRecommendationsQuery {
    status?: RecommendationStatus;
    type?: RecommendationType;
    page?: number;
    limit?: number;
}

export const fetchRecommendations = async (
    businessId: string,
    query: IListRecommendationsQuery = {},
): Promise<IRecommendationListResponse> => {
    try {
        const params: Record<string, any> = {};
        if (query.status) params.status = query.status;
        if (query.type) params.type = query.type;
        if (query.page) params.page = query.page;
        if (query.limit) params.limit = query.limit;
        const res = await axios.get(
            `${API_BASE_URL}/collect/intelligence/recommendations`,
            withBusiness(businessId, { params }),
        );
        return res.data?.data as IRecommendationListResponse;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load recommendations."));
    }
};

export const recomputeIntelligence = async (
    businessId: string,
): Promise<IRecomputeIntelligenceResult> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/intelligence/recompute`,
            {},
            withBusiness(businessId),
        );
        return res.data?.data as IRecomputeIntelligenceResult;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to recompute intelligence."));
    }
};

export const applyRecommendation = async (
    businessId: string,
    recommendationId: string,
): Promise<IRecommendation> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/intelligence/recommendations/${recommendationId}/apply`,
            {},
            withBusiness(businessId),
        );
        return res.data?.data as IRecommendation;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to apply recommendation."));
    }
};

export const dismissRecommendation = async (
    businessId: string,
    recommendationId: string,
    reason?: string,
): Promise<IRecommendation> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/intelligence/recommendations/${recommendationId}/dismiss`,
            { reason },
            withBusiness(businessId),
        );
        return res.data?.data as IRecommendation;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to dismiss recommendation."));
    }
};
